import { AppBar, Toolbar, IconButton, Menu, MenuItem, Box, Typography, Autocomplete, TextField, CircularProgress, InputAdornment } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import KeyIcon from '@mui/icons-material/Key';
import GalleryIcon from '@mui/icons-material/PhotoLibrary';
import TaskIcon from '@mui/icons-material/Task';
import NoteIcon from '@mui/icons-material/Note';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SourceIcon from '@mui/icons-material/Source';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import TranslateIcon from '@mui/icons-material/Translate';
import { LANGUAGE_STORAGE_KEY } from '../i18n/config';
import React, { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getNotes } from '../api/notes';
import { Note } from '../types/note';
import { useSnackbar } from 'notistack';
import { authService } from '../api/auth';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider, Collapse } from '@mui/material';
import { useMediaQuery } from '@mui/material';

const defaultMenu = '{"dashboard":true,"api-tokens":true,"gallery":true,"gallery-slider":true,"tasks":true,"notes":true,"mind-map-notes":true,"accounting":true,"file-manager":true,"external-assets":true,"projects":true,"path-opener":true}';
const menu = JSON.parse(import.meta.env.VITE_APP_MENU || defaultMenu);
/** Items added after VITE_APP_MENU was configured: visible unless explicitly disabled there. */
const NEW_MENU_DEFAULTS: Record<string, boolean> = { music: true };
const isMenuVisible = (key: string): boolean => Boolean(menu[key] ?? NEW_MENU_DEFAULTS[key] ?? false);

export const Navbar = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const toggleLanguage = () => {
        const next = i18n.language === 'sk' ? 'en' : 'sk';
        void i18n.changeLanguage(next);
        try {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
        } catch {
            // ignore storage errors
        }
    };
    const otherLanguageLabel = i18n.language === 'sk' ? t('language.en') : t('language.sk');
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mindAnchor, setMindAnchor] = useState<null | HTMLElement>(null);
    const [mobileMindOpen, setMobileMindOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width:1024px)');

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        authService.logout();
        try {
            // Remove cached user so ProtectedRoute stops treating user as authenticated
            queryClient.removeQueries({ queryKey: ['user'] });
        } catch (err) {
            console.error('Error removing user query cache', err);
        }
        navigate('/login');
        enqueueSnackbar(t('dashboard.logoutSuccess'), { variant: 'success' });
    };

    const toggleMobile = () => setMobileOpen((prev) => !prev);
    const handleMindOpen = (event: React.MouseEvent<HTMLElement>) => setMindAnchor(event.currentTarget);
    const handleMindClose = () => setMindAnchor(null);
    const toggleMobileMind = () => setMobileMindOpen(prev => !prev);

    const openMobileRouteInNewTab = (path: string) => {
        window.open(path, '_blank', 'noopener,noreferrer');
        setMobileOpen(false);
    };

    const menuItems = [
        { text: t('dashboard.title'), key: 'dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: t('apiTokens.title'), key: 'api-tokens', icon: <KeyIcon />, path: '/api-tokens' },
        { text: t('gallery.title'), key: 'gallery', icon: <GalleryIcon />, path: '/gallery' },
        { text: t('tasks.title'), key: 'tasks', icon: <TaskIcon />, path: '/tasks' },
        { text: t('notes.title'), key: 'notes', icon: <NoteIcon />, path: '/notes' },
        { text: 'Mind Map', key: 'mind-map-notes', icon: <CloudSyncIcon />, path: '/mind-map-notes' },
        { text: t('accounting.title') || 'Accounting', key: 'accounting', icon: <AccountBalanceIcon />, path: '/accounting' },
        { text: t('fileManager.title'), key: 'file-manager', icon: <SourceIcon />, path: '/file-manager' },
        { text: t('externalAssets.title'), key: 'external-assets', icon: <Inventory2Icon />, path: '/external-assets' },
        { text: t('projects.title') || 'Projects', key: 'projects', icon: <FolderCopyIcon />, path: '/projects' },
        { text: t('pathOpener.title'), key: 'path-opener', icon: <OpenInNewIcon />, path: '/path-opener' },
        { text: t('music.title'), key: 'music', icon: <MusicNoteIcon />, path: '/music' },
    ];

    const [searchOptions, setSearchOptions] = useState<Note[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchTimeout = useRef<number | null>(null);

    const fetchNotesForAutocomplete = async (q: string) => {
        if (!q || q.trim().length === 0) {
            setSearchOptions([]);
            setSearchLoading(false);
            return;
        }
        setSearchLoading(true);
        try {
            const filters = `filters[name][$contains]=${encodeURIComponent(q)}&filters[description][$contains]=${encodeURIComponent(q)}`;
            const data = await getNotes({ filters, per_page: 10 });
            setSearchOptions(data.notes || []);
        } catch (err) {
            console.error('Error fetching notes for autocomplete', err);
            setSearchOptions([]);
        } finally {
            setSearchLoading(false);
        }
    };

    return (
        <AppBar position="fixed">
            <Toolbar>
                {isMobile && (
                    <IconButton color="inherit" edge="start" onClick={toggleMobile} sx={{ mr: 2 }}>
                        <MenuIcon />
                    </IconButton>
                )}
                <Box sx={{ flexGrow: 1, display: isMobile ? 'none' : 'flex', gap: 2 }}>
                    {menuItems.map((item) => (
                        // render normal items (mind-map-notes will be handled separately)
                        item.key !== 'mind-map-notes' && isMenuVisible(item.key) && (
                            <Link
                                key={item.path}
                                to={item.path}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                }}
                            >
                                {item.icon}
                                <Typography sx={{ ml: 1 }}>
                                    {item.text}
                                </Typography>
                            </Link>
                        )
                    ))}

                    {/* Mind Map top menu (dropdown with two submenu items) */}
                    {menu['mind-map-notes'] && (
                        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <Box component="a" onClick={handleMindOpen} sx={{ display: 'flex', alignItems: 'center', px: 1, cursor: 'pointer', color: 'inherit', textDecoration: 'none' }}>
                                <CloudSyncIcon />
                                <Typography sx={{ ml: 1 }}>Mind Map</Typography>
                            </Box>
                            <Menu
                                anchorEl={mindAnchor}
                                open={Boolean(mindAnchor)}
                                onClose={handleMindClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            >
                                <MenuItem component={Link} to="/mind-map-notes" onClick={handleMindClose}>Notes View</MenuItem>
                                <MenuItem component={Link} to="/mind-map" onClick={handleMindClose}>Mind Map</MenuItem>
                            </Menu>
                        </Box>
                    )}
                </Box>
                <div>
                    {/* Notes quick search */}
                    <Box sx={{ display: { xs: 'none', md: 'inline-flex' }, alignItems: 'center', mr: 2 }}>
                        <Autocomplete
                            size="small"
                            sx={{ width: 320, '& .MuiInputBase-root': { bgcolor: '#F1F8FF', borderRadius: 1, px: 0.5 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' }, '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' } }}
                            freeSolo
                            options={searchOptions}
                            loading={searchLoading}
                            filterOptions={(x) => x}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                            onChange={(_e, value) => {
                                if (!value) return;
                                const note = value as Note;
                                navigate(`/notes/${note.id}`);
                            }}
                            onInputChange={(_e, value) => {
                                if (searchTimeout.current) window.clearTimeout(searchTimeout.current);
                                searchTimeout.current = window.setTimeout(() => fetchNotesForAutocomplete(value), 300) as unknown as number;
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('notes.search')}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: 'primary.main', opacity: 0.9 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <>
                                                {searchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps?.endAdornment}
                                            </>
                                        )
                                    }}
                                    inputProps={{ ...(params.inputProps || {}), id: 'global-note-search-input' }}
                                />
                            )}
                        />
                    </Box>
                    <IconButton
                        size="large"
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={handleMenu}
                        color="inherit"
                    >
                        <AccountCircleIcon />
                    </IconButton>
                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        <MenuItem component={Link} to="/account" onClick={handleClose}>
                            {t('account.title')}
                        </MenuItem>
                        <MenuItem onClick={() => { handleClose(); toggleLanguage(); }}>
                            <TranslateIcon fontSize="small" sx={{ mr: 1 }} />
                            {otherLanguageLabel}
                        </MenuItem>
                        <MenuItem onClick={() => { handleClose(); handleLogout(); }}>{t('dashboard.logout')}</MenuItem>
                    </Menu>
                </div>
            </Toolbar>
            <Drawer anchor="left" open={mobileOpen} onClose={toggleMobile}>
                <Box sx={{ width: 280 }} role="presentation" onClick={toggleMobile} onKeyDown={toggleMobile}>
                    {/* Mobile search (visible in drawer) */}
                    <Box sx={{ p: 2, display: { xs: 'block', md: 'none' } }} onClick={(e) => e.stopPropagation()}>
                        <Autocomplete
                            size="small"
                            freeSolo
                            options={searchOptions}
                            loading={searchLoading}
                            filterOptions={(x) => x}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                            onChange={(_e, value) => {
                                if (!value) return;
                                const note = value as Note;
                                navigate(`/notes/${note.id}`);
                                setMobileOpen(false);
                            }}
                            onInputChange={(_e, value) => {
                                if (searchTimeout.current) window.clearTimeout(searchTimeout.current);
                                searchTimeout.current = window.setTimeout(() => fetchNotesForAutocomplete(value), 300) as unknown as number;
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('notes.search')}
                                    size="small"
                                    InputProps={{ ...params.InputProps, endAdornment: (
                                        <>
                                            {searchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps?.endAdornment}
                                        </>
                                    ) }}
                                    inputProps={{ ...(params.inputProps || {}), id: 'global-note-search-input', onMouseDown: (e: React.MouseEvent) => e.stopPropagation() }}
                                />
                            )}
                        />
                    </Box>
                    <List>
                        {menuItems.map((item) => (
                           item.key !== 'mind-map-notes' && isMenuVisible(item.key) && (
                               <ListItemButton key={item.path} onClick={() => openMobileRouteInNewTab(item.path)} selected={location.pathname === item.path}>
                                  <ListItemIcon>{item.icon}</ListItemIcon>
                                  <ListItemText primary={item.text} />
                               </ListItemButton>
                           )
                        ))}

                        {/* Mobile: Mind Map expandable group */}
                        {menu['mind-map-notes'] && (
                            <>
                                <ListItemButton onClick={toggleMobileMind}>
                                    <ListItemIcon><CloudSyncIcon /></ListItemIcon>
                                    <ListItemText primary={'Mind Map'} />
                                    {mobileMindOpen ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                                <Collapse in={mobileMindOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        <ListItemButton sx={{ pl: 4 }} onClick={() => openMobileRouteInNewTab('/mind-map-notes')} selected={location.pathname === '/mind-map-notes'}>
                                            <ListItemIcon><NoteIcon /></ListItemIcon>
                                            <ListItemText primary={'Notes View'} />
                                        </ListItemButton>
                                        <ListItemButton sx={{ pl: 4 }} onClick={() => openMobileRouteInNewTab('/mind-map')} selected={location.pathname === '/mind-map'}>
                                            <ListItemIcon><CloudSyncIcon /></ListItemIcon>
                                            <ListItemText primary={'Mind Map'} />
                                        </ListItemButton>
                                    </List>
                                </Collapse>
                            </>
                        )}
                    </List>
                    <Divider />
                    <List>
                        <ListItemButton onClick={() => { setMobileOpen(false); navigate('/account'); }}>
                            <ListItemText primary={t('account.title')} />
                        </ListItemButton>
                        <ListItemButton onClick={toggleLanguage}>
                            <ListItemIcon><TranslateIcon /></ListItemIcon>
                            <ListItemText primary={otherLanguageLabel} />
                        </ListItemButton>
                        <ListItemButton onClick={handleLogout}>
                            <ListItemText primary={t('dashboard.logout')} />
                        </ListItemButton>
                    </List>
                </Box>
            </Drawer>
        </AppBar>
    );
}; 
